import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import {
  getTags,
  getTagDefinitions,
  addTags,
  removeTags,
  createTagDefinition,
  type TagOut,
  type TagDefinition,
} from "../api/portfolio";

export function useTags() {
  const { token } = useAuth();
  return useQuery<TagOut[]>({
    queryKey: ["portfolio-tags"],
    queryFn: () => getTags(token!),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
  });
}

export function useTagDefinitions() {
  const { token } = useAuth();
  return useQuery<TagDefinition[]>({
    queryKey: ["portfolio-tag-definitions"],
    queryFn: () => getTagDefinitions(token!),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAddTags() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { projectIds: string[]; tags: { key: string; value: string }[] }) =>
      addTags(token!, args.projectIds, args.tags),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["portfolio-tags"] });
      qc.invalidateQueries({ queryKey: ["portfolio-summary"] });
      qc.invalidateQueries({ queryKey: ["portfolio-overview"] });
      qc.invalidateQueries({ queryKey: ["portfolio-projects"] });
      variables.projectIds.forEach((id) =>
        qc.invalidateQueries({ queryKey: ["project-tags", id] }),
      );
    },
  });
}

export function useRemoveTags() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { projectIds: string[]; tags: { key: string; value: string }[] }) =>
      removeTags(token!, args.projectIds, args.tags),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["portfolio-tags"] });
      qc.invalidateQueries({ queryKey: ["portfolio-summary"] });
      qc.invalidateQueries({ queryKey: ["portfolio-overview"] });
      qc.invalidateQueries({ queryKey: ["portfolio-projects"] });
      variables.projectIds.forEach((id) =>
        qc.invalidateQueries({ queryKey: ["project-tags", id] }),
      );
    },
  });
}

export function useCreateTagDefinition() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { tag_key: string; display_name?: string; color?: string; sort_order?: number }) =>
      createTagDefinition(token!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-tag-definitions"] });
    },
  });
}
